/**
 * Human-readable formatting for student change-history From/To values.
 */

const FIELD_LABELS = {
  academicQualifications: 'Education / qualifications',
  fullName: 'Full name',
  emailAddress: 'Email',
  phoneNumber: 'Phone',
  currentAddress: 'Current address',
  permanentAddress: 'Permanent address',
  currentLocation: 'Current location',
  parentsName: 'Parents name',
  parentsPhone: 'Parents phone',
  parentsEmail: 'Parents email',
  employeeId: 'Employee ID',
  experienceYears: 'Experience (years)',
  profilePreference: 'Profile preference',
  diplomaDone: 'Diploma done',
  joiningKitReceived: 'Joining kit received',
  joiningKitAddress: 'Joining kit address',
  domainId: 'Domain',
  countryId: 'Country',
  stateId: 'State',
  cityId: 'City',
  heardFromId: 'Heard from',
  resumeUrl: 'Resume URL',
  linkedIn: 'LinkedIn',
  batchStatus: 'Batch status',
  placementStatus: 'Placement status',
  attendancePercentage: 'Attendance %',
};

function humanizeFieldKey(field) {
  if (!field) return 'Field';
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  return String(field)
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^\s+/, '')
    .replace(/^./, (c) => c.toUpperCase());
}

function parseMaybeJson(value) {
  if (value == null || value === '') return value;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (
    (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
    (trimmed.startsWith('{') && trimmed.endsWith('}'))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }
  return value;
}

function formatMarks(qual) {
  const marks = qual.percentageCgpa ?? qual.percentage_cgpa;
  const type = qual.marksType || qual.marks_type;
  if (marks == null || marks === '') return null;
  if (type === 'CGPA') return `CGPA ${marks}`;
  if (type === 'Percentage') return `${marks}%`;
  return type ? `${marks} (${type})` : String(marks);
}

function formatOneQualification(qual) {
  if (!qual || typeof qual !== 'object') return null;
  const type = qual.qualificationType || qual.qualification_type || 'Qualification';
  const parts = [type];

  const marks = formatMarks(qual);
  if (marks) parts.push(marks);

  const year = qual.yearOfPassout ?? qual.year_of_passout;
  if (year != null && year !== '') parts.push(`year ${year}`);

  const college = qual.universityCollegeName || qual.university_college_name;
  if (college) parts.push(`at ${college}`);

  const branch = qual.specializationBranch || qual.specialization_branch;
  if (branch) parts.push(`specialization: ${branch}`);

  if (qual.isHighestDegree || qual.is_highest_degree) parts.push('(highest degree)');

  if (qual.hasGapInEducation || qual.has_gap_in_education) {
    const why = qual.gapInEducationJustification || qual.gap_in_education_justification;
    parts.push(why ? `gap in education: ${why}` : 'had education gap');
  }
  if (qual.hasGapAfterHighestEducation || qual.has_gap_after_highest_education) {
    const why =
      qual.gapAfterHighestEducationJustification ||
      qual.gap_after_highest_education_justification;
    parts.push(why ? `gap after highest: ${why}` : 'had gap after highest education');
  }

  return parts.join(' · ');
}

function qualificationsByType(list) {
  const map = new Map();
  if (!Array.isArray(list)) return map;
  for (const q of list) {
    const type = q?.qualificationType || q?.qualification_type;
    if (type) map.set(String(type), q);
  }
  return map;
}

/**
 * Summarize an academic qualifications array for display.
 */
export function formatAcademicQualificationsReadable(list) {
  const parsed = parseMaybeJson(list);
  if (!Array.isArray(parsed) || parsed.length === 0) return 'None';
  const lines = parsed.map(formatOneQualification).filter(Boolean);
  return lines.length ? lines.join('\n') : 'None';
}

/**
 * Prefer showing only what changed between two academic qualification snapshots.
 */
export function formatAcademicQualificationsDiff(fromValue, toValue) {
  const fromList = parseMaybeJson(fromValue);
  const toList = parseMaybeJson(toValue);
  if (!Array.isArray(fromList) && !Array.isArray(toList)) return null;

  const fromMap = qualificationsByType(fromList || []);
  const toMap = qualificationsByType(toList || []);
  const types = new Set([...fromMap.keys(), ...toMap.keys()]);
  const changes = [];

  for (const type of types) {
    const a = fromMap.get(type);
    const b = toMap.get(type);
    if (!a && b) {
      changes.push(`Added ${formatOneQualification(b)}`);
      continue;
    }
    if (a && !b) {
      changes.push(`Removed ${formatOneQualification(a)}`);
      continue;
    }
    const before = formatOneQualification(a);
    const after = formatOneQualification(b);
    if (before !== after) {
      const detailBits = [];
      const fields = [
        ['marks', formatMarks],
        ['year', (q) => {
          const y = q.yearOfPassout ?? q.year_of_passout;
          return y != null && y !== '' ? String(y) : null;
        }],
        ['college', (q) => q.universityCollegeName || q.university_college_name || null],
        ['specialization', (q) => q.specializationBranch || q.specialization_branch || null],
      ];
      for (const [label, getter] of fields) {
        const av = getter(a) || '—';
        const bv = getter(b) || '—';
        if (av !== bv) detailBits.push(`${label}: ${av} → ${bv}`);
      }
      if (detailBits.length) {
        changes.push(`${type}: ${detailBits.join('; ')}`);
      } else {
        changes.push(`${type}: ${before} → ${after}`);
      }
    }
  }

  if (!changes.length) {
    return {
      fromText: formatAcademicQualificationsReadable(fromList),
      toText: formatAcademicQualificationsReadable(toList),
      summary: null,
    };
  }

  return {
    fromText: formatAcademicQualificationsReadable(fromList),
    toText: formatAcademicQualificationsReadable(toList),
    summary: changes.join('\n'),
  };
}

function looksLikeAcademicList(value) {
  const parsed = parseMaybeJson(value);
  if (!Array.isArray(parsed) || parsed.length === 0) return false;
  return parsed.some(
    (item) =>
      item &&
      typeof item === 'object' &&
      (item.qualificationType || item.qualification_type)
  );
}

/**
 * Format a single From/To cell value.
 */
export function formatStudentChangeValue(value, field) {
  if (value == null || value === '') return '—';

  if (field === 'academicQualifications' || looksLikeAcademicList(value)) {
    return formatAcademicQualificationsReadable(value);
  }

  const parsed = parseMaybeJson(value);
  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return 'None';
    return parsed
      .map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item)))
      .join(', ');
  }
  if (parsed && typeof parsed === 'object') {
    return Object.entries(parsed)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => `${humanizeFieldKey(k)}: ${v}`)
      .join(' · ') || '—';
  }

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function formatStudentChangeFieldLabel(field) {
  return humanizeFieldKey(field);
}

/**
 * Build display rows for a change entry (may collapse academics into a clearer diff).
 */
export function buildStudentChangeDisplayRows(change) {
  const field = change?.field;
  const from = change?.from;
  const to = change?.to;

  if (field === 'academicQualifications' || looksLikeAcademicList(from) || looksLikeAcademicList(to)) {
    const diff = formatAcademicQualificationsDiff(from, to);
    if (diff?.summary) {
      return [
        {
          fieldLabel: formatStudentChangeFieldLabel(field || 'academicQualifications'),
          fromText: diff.fromText,
          toText: diff.toText,
          highlight: diff.summary,
        },
      ];
    }
  }

  return [
    {
      fieldLabel: formatStudentChangeFieldLabel(field),
      fromText: formatStudentChangeValue(from, field),
      toText: formatStudentChangeValue(to, field),
      highlight: null,
    },
  ];
}
